import { Controller } from "@hotwired/stimulus";

// GR-3427: section 3 of the Advanced Schedule Builder, the rooms each day of
// the meeting needs.
//
// Every row of every day is server-rendered and each day's blank row lives in
// its own <template>, so with JS disabled every requirement already saved is
// still editable and every day still offers one empty line. Only adding,
// removing and copying a day onto another need this controller.
//
// One controller for the whole section rather than one per day, because Copy
// day to... reaches across day groups, and because the rows all post into one
// event_request[space_needs_attributes] list: two days counting their own
// indices would collide there and post as one row.
export default class EventRequestSpaceNeedsController extends Controller {
  static targets = ["dayGroup", "rows", "row", "template", "copyMenu", "note", "noteToggle", "timeCell", "purposeField", "setupLabelField"];

  // GR-3305: the function type select's last option, which posts as no term.
  static OTHER = "other";

  // The fields a day hands to another day. Not id, not _destroy, not
  // event_day_id and not position: those say which row this is and where it
  // sits, which is exactly what the copy is changing.
  static COPIED_FIELDS = [
    "function_type_term_id",
    "purpose",
    "start_time",
    "end_time",
    "headcount",
    "room_setup_term_id",
    "room_setup_label",
    "quantity",
    "notes",
  ];

  connect() {
    this.refresh();
  }

  addRow(event) {
    const group = this.groupFor(event.target);
    if (!group) return;

    const row = this.appendRow(group);
    this.refresh();
    (this.fieldIn(row, "function_type_term_id") ?? this.fieldIn(row, "purpose"))?.focus();
  }

  appendRow(group) {
    const template = this.templateTargets.find((candidate) => this.groupFor(candidate) === group);
    const rows = this.rowsFor(group);
    if (!template || !rows) return null;

    const html = template.innerHTML.replace(/__INDEX__/g, String(this.nextIndex()));
    rows.insertAdjacentHTML("beforeend", html);
    return rows.lastElementChild;
  }

  // A saved requirement is marked for destruction and hidden, so the server is
  // told about the deletion. An unsaved row just leaves the DOM.
  removeRow(event) {
    const row = event.target.closest("[data-event-request-space-needs-target='row']");
    if (!row) return;

    if (row.querySelector("input[name$='[id]']")) {
      row.querySelector("input[name$='[_destroy]']").value = "1";
      row.hidden = true;
    } else {
      row.remove();
    }
    this.refresh();
  }

  // Most meetings run the same shape of day more than once, so a day that has
  // been filled in can hand its rows to the others. The rows are written into
  // the target day's form rather than saved, so the copy is still reviewable
  // and undoable before the step is.
  //
  // Appended rather than substituted: a day the requester has already answered
  // for keeps its answers, and the copy lands under them.
  copyDay(event) {
    const source = this.groupById(event.params.sourceDay);
    if (!source) return;

    const filled = this.filledRows(source);
    if (filled.length === 0) return;

    this.copyTargets(source, event.params.targetDay).forEach((target) => {
      filled.forEach((row) => this.writeRow(this.blankRow(target) ?? this.appendRow(target), row));
    });
    this.refresh();
  }

  copyTargets(source, targetDay) {
    if (String(targetDay) === "all") {
      return this.dayGroupTargets.filter((group) => group !== source);
    }

    const target = this.groupById(targetDay);
    return target && target !== source ? [target] : [];
  }

  // A row nobody has named a function or a purpose in. The blank line every
  // day opens on is one, and filling it first keeps a copy from leaving a hole
  // above the rows it wrote.
  blankRow(group) {
    return this.rowsIn(group).find((row) => !row.hidden && !this.named(row));
  }

  filledRows(group) {
    return this.rowsIn(group).filter((row) => !row.hidden && this.named(row));
  }

  // GR-3305: a function type names the row on its own; Other names nothing
  // until its label is typed.
  named(row) {
    const type = this.fieldIn(row, "function_type_term_id")?.value;
    if (type && type !== EventRequestSpaceNeedsController.OTHER) return true;

    return Boolean(this.fieldIn(row, "purpose")?.value);
  }

  writeRow(target, source) {
    if (!target) return;

    EventRequestSpaceNeedsController.COPIED_FIELDS.forEach((name) => {
      const from = this.fieldIn(source, name);
      const to = this.fieldIn(target, name);
      if (from && to) to.value = from.value;
    });
  }

  // Called after every mutation and on every keystroke in a Purpose field.
  // Positions are DOM order within the day, which is the order the reader put
  // the rows in; and a day with nothing in it has nothing to copy, so its
  // menu is not offered.
  refresh() {
    this.dayGroupTargets.forEach((group) => {
      this.rowsIn(group)
        .filter((row) => !row.hidden)
        .forEach((row, position) => {
          const field = row.querySelector("input[name$='[position]']");
          if (field) field.value = String(position);
        });

      const menu = this.copyMenuTargets.find((candidate) => this.groupFor(candidate) === group);
      if (menu) menu.hidden = this.filledRows(group).length === 0;
    });

    this.rowTargets.forEach((row) => {
      this.markNote(row);
      this.markTimeCell(row);
      this.markPurpose(row);
      this.markSetupLabel(row);
    });
  }

  // GR-3306: the set-up's Other works as the function's does, with a label of
  // its own. A set-up alone does not name a row, so this does not refresh the
  // copy menus.
  roomSetupChanged(event) {
    const row = event.target.closest("[data-event-request-space-needs-target='row']");
    if (!row) return;

    this.markSetupLabel(row);
    if (event.target.value === EventRequestSpaceNeedsController.OTHER) this.fieldIn(row, "room_setup_label")?.focus();
  }

  markSetupLabel(row) {
    const field = row.querySelector("[data-event-request-space-needs-target='setupLabelField']");
    const select = this.fieldIn(row, "room_setup_term_id");
    if (!field || !select) return;

    field.hidden = select.value !== EventRequestSpaceNeedsController.OTHER;
  }

  // GR-3305: Other opens the custom label beside the select, and puts the
  // cursor in it, since a label is what Other is asking for.
  functionTypeChanged(event) {
    const row = event.target.closest("[data-event-request-space-needs-target='row']");
    if (!row) return;

    this.refresh();
    if (event.target.value === EventRequestSpaceNeedsController.OTHER) this.fieldIn(row, "purpose")?.focus();
  }

  // The label shows for Other, and for a row without the select at all (an
  // organization with no list). A row with a label but no choice is one the
  // copy wrote from a day saved that way, and keeps its label in sight.
  markPurpose(row) {
    const field = row.querySelector("[data-event-request-space-needs-target='purposeField']");
    if (!field) return;

    const select = this.fieldIn(row, "function_type_term_id");
    const type = select?.value;
    field.hidden = Boolean(select) && type !== EventRequestSpaceNeedsController.OTHER &&
      !(type === "" && this.fieldIn(row, "purpose")?.value);
  }

  // GR-3438: the note folds away behind its own button, so the button has to
  // say whether there is anything behind it. Colour alone would not: the
  // label carries it too.
  toggleNote(event) {
    const row = event.target.closest("[data-event-request-space-needs-target='row']");
    const note = this.noteIn(row);
    if (!note) return;

    note.hidden = !note.hidden;
    if (!note.hidden) this.fieldIn(row, "notes")?.focus();
    this.markNote(row);
  }

  markNote(row) {
    const toggle = this.toggleIn(row);
    const note = this.noteIn(row);
    if (!toggle || !note) return;

    const written = Boolean(this.fieldIn(row, "notes")?.value);
    toggle.setAttribute("aria-expanded", String(!note.hidden));
    toggle.setAttribute("aria-label", written ? "Edit note" : "Add a note");

    const icon = toggle.querySelector("svg");
    if (!icon) return;
    icon.classList.toggle("color-fg-accent", written);
    icon.classList.toggle("color-fg-muted", !written);
  }

  // GR-3438: both times blank is All day, and the cell says it once rather
  // than showing two empty boxes.
  //
  // GR-3445: this only ever opens the cell. Folding it is clearTimes' job
  // alone, because emptying a field is usually the first half of retyping it,
  // and taking the inputs away under the cursor is not a kindness. A row that
  // means All day and says so keeps saying so until somebody presses the
  // words.
  markTimeCell(row) {
    const cell = this.cellIn(row);
    if (!cell) return;

    const given = ["start_time", "end_time"].some((name) => this.fieldIn(row, name)?.value);
    if (given) {
      cell.dataset.allDay = "false";
    } else if (!cell.dataset.allDay) {
      cell.dataset.allDay = "true";
    }
  }

  // GR-3445: the way in. A new row is born with both times blank, so the
  // collapsed cell is all it has; the words are what opens it.
  revealTimes(event) {
    const row = event.target.closest("[data-event-request-space-needs-target='row']");
    const cell = this.cellIn(row);
    if (!cell) return;

    cell.dataset.allDay = "false";
    this.fieldIn(row, "start_time")?.focus();
  }

  // The way back to All day. A time input can be cleared from the keyboard,
  // but not every browser offers a control for it, and the reader should not
  // have to know that.
  clearTimes(event) {
    const row = event.target.closest("[data-event-request-space-needs-target='row']");
    if (!row) return;

    ["start_time", "end_time"].forEach((name) => {
      const field = this.fieldIn(row, name);
      if (field) field.value = "";
    });

    // Said outright rather than left to markTimeCell, which since GR-3445
    // only ever opens the cell. This is the one place that folds it.
    const cell = this.cellIn(row);
    if (cell) cell.dataset.allDay = "true";
  }

  // The inputs are native, so they say `change` as they are edited and it
  // bubbles: the section hears every row's without wiring each one.
  timeChanged(event) {
    const row = event.target.closest("[data-event-request-space-needs-target='row']");
    if (row) this.markTimeCell(row);
  }

  noteIn(row) {
    return row?.querySelector("[data-event-request-space-needs-target='note']");
  }

  toggleIn(row) {
    return row?.querySelector("[data-event-request-space-needs-target='noteToggle']");
  }

  cellIn(row) {
    return row?.querySelector("[data-event-request-space-needs-target='timeCell']");
  }

  groupFor(element) {
    return element?.closest("[data-event-request-space-needs-target='dayGroup']");
  }

  groupById(dayId) {
    return this.dayGroupTargets.find((group) => group.dataset.eventDayId === String(dayId));
  }

  rowsFor(group) {
    return this.rowsTargets.find((rows) => this.groupFor(rows) === group);
  }

  rowsIn(group) {
    return this.rowTargets.filter((row) => this.groupFor(row) === group);
  }

  // Matches an input or a select, because the times are selects and the rest
  // are inputs.
  fieldIn(row, name) {
    return row?.querySelector(`[name$='[${name}]']`);
  }

  // Index only has to be unique within the submitted params, so keep counting
  // up past rows that have been removed. Counted across the whole section:
  // every day's rows share one list.
  nextIndex() {
    this.rowCounter = Math.max(this.rowCounter ?? 0, this.rowTargets.length);
    return this.rowCounter++;
  }
}
